/* eslint-disable react-compiler/react-compiler */

import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronsDown, ChevronsUp } from 'lucide-react'
import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { ButtonSelectFormField } from 'src/shared/components/ui/forms/form-fields/button-select'
import { CountFormField } from 'src/shared/components/ui/forms/form-fields/count'
import { PriceRangeFormField } from 'src/shared/components/ui/forms/form-fields/price-range'
import { SelectFormField } from 'src/shared/components/ui/forms/form-fields/select'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { propertyTypeMap, propertyTypeSchema, timeframeMap, timeframeSchema } from 'src/shared/schemas/referrals'
import type { ClientType } from 'src/shared/schemas/referrals/referral-fields/real-estate-agent/client-type'
import {
  clientTypeMap,
  clientTypeSchema,
} from 'src/shared/schemas/referrals/referral-fields/real-estate-agent/client-type'
import { useEventStore } from 'src/shared/store/event-store'

// Base schema with shared fields
const baseSchema = z.object({
  clientType: clientTypeSchema,
  timeframe: timeframeSchema.optional(),
})

// Buy-specific fields
const buySchema = z.object({
  priceRange: z.object({
    min: z.number().min(0),
    max: z.number().min(0),
  }),
  propertyType: propertyTypeSchema,
})

// Sell-specific fields
const sellSchema = z.object({
  priceRange: z.object({
    min: z.number().min(0),
    max: z.number().min(0),
  }),
  propertyType: propertyTypeSchema,
})

// Lease-specific fields
const leaseSchema = z.object({
  budget: z.object({
    min: z.number().min(0),
    max: z.number().min(0),
  }),
  bedrooms: z.number().min(0),
  bathrooms: z.number().min(0),
})

// Combined schema with discriminated union
const realEstateAgentSchema = z.discriminatedUnion('clientType', [
  baseSchema
    .extend({
      clientType: z.literal('buy'),
    })
    .merge(buySchema),
  baseSchema
    .extend({
      clientType: z.literal('sell'),
    })
    .merge(sellSchema),
  baseSchema
    .extend({
      clientType: z.literal('lease'),
    })
    .merge(leaseSchema),
])

type RealEstateAgentFormData = z.infer<typeof realEstateAgentSchema>

export const RealEstateAgentInfo: FC = () => {
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const selectedReferral = useSendReferralStore((state) => state.selectedReferral)
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const [showOptional, setShowOptional] = useState(false)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId

  const baseDefaultValues = {
    clientType: referralFormData.clientType || selectedReferral?.clientType || 'buy',
    timeframe: referralFormData.timeframe || selectedReferral?.timeframe || undefined,
  }

  // Type-specific default values
  const typeSpecificDefaults = useMemo(
    () => ({
      buy: {
        clientType: 'buy' as ClientType,
        priceRange: {
          min: referralFormData.priceRange?.min || selectedReferral?.priceRange?.min || 100000,
          max: referralFormData.priceRange?.max || selectedReferral?.priceRange?.max || 450000,
        },
        propertyType: referralFormData.propertyType || selectedReferral?.propertyType || undefined,
      },
      sell: {
        clientType: 'sell' as ClientType,
        priceRange: {
          min: referralFormData.priceRange?.min || selectedReferral?.priceRange?.min || 100000,
          max: referralFormData.priceRange?.max || selectedReferral?.priceRange?.max || 450000,
        },
        propertyType: referralFormData.propertyType || selectedReferral?.propertyType || undefined,
      },
      lease: {
        clientType: 'lease' as ClientType,
        budget: {
          min: referralFormData.budget?.min || selectedReferral?.budget?.min || 200,
          max: referralFormData.budget?.max || selectedReferral?.budget?.max || 20000,
        },
        bedrooms: referralFormData.bedrooms || selectedReferral?.bedrooms || undefined,
        bathrooms: referralFormData.bathrooms || selectedReferral?.bathrooms || undefined,
      },
    }),
    [referralFormData, selectedReferral],
  )

  const form = useForm<RealEstateAgentFormData>({
    resolver: zodResolver(realEstateAgentSchema),
    mode: 'onSubmit',
    defaultValues: {
      ...baseDefaultValues,
      ...(selectedReferral?.clientType ? typeSpecificDefaults[selectedReferral.clientType] : typeSpecificDefaults.buy),
    },
  })

  // Reset form fields when client type changes
  const clientType = form.watch('clientType')

  useEffect(() => {
    const newValues = {
      ...form.getValues(),
      ...typeSpecificDefaults[clientType as ClientType],
      timeframe: form.getValues('timeframe'),
    }
    form.reset(newValues as RealEstateAgentFormData)
  }, [clientType, form, typeSpecificDefaults])

  const onSubmit = async (data: RealEstateAgentFormData) => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'submit_real_estate_agent_info',
          flowId,
          ...data,
        },
      })
    }

    handleNext(data)
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mb-24 space-y-6">
        {/* Base Fields */}
        <ButtonSelectFormField
          control={form.control}
          name="clientType"
          label="Client Type"
          allowDeselect={false}
          gridCols={3}
          options={clientTypeMap}
          required
        />
        {/* Conditional Fields */}
        {clientType === 'buy' && (
          <>
            <PriceRangeFormField control={form.control} name="priceRange" label="Price Range" required />
            <SelectFormField
              control={form.control}
              name="propertyType"
              label="Property Type"
              options={propertyTypeMap}
            />
          </>
        )}

        {clientType === 'sell' && (
          <>
            <PriceRangeFormField control={form.control} name="priceRange" label="Price Range" required />
            <SelectFormField
              control={form.control}
              name="propertyType"
              label="Property Type"
              options={propertyTypeMap}
            />
          </>
        )}

        {clientType === 'lease' && (
          <>
            <PriceRangeFormField
              control={form.control}
              name="budget"
              label="Budget Range"
              min={200}
              max={20000}
              step={100}
              required
            />
            <div className="flex w-full flex-row justify-between gap-16">
              <CountFormField control={form.control} name="bedrooms" label="Bedroom(s)" />
              <CountFormField control={form.control} name="bathrooms" label="Bathroom(s)" increment={0.5} />
            </div>
          </>
        )}
        <div className="flex w-full justify-center">
          <Button
            variant="link"
            type="button"
            onClick={() => setShowOptional(!showOptional)}
            className="flex items-center justify-center gap-2"
          >
            {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />} Optional Info{' '}
            {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />}
          </Button>
        </div>

        {showOptional && (
          <SelectFormField control={form.control} name="timeframe" label="Timeframe" options={timeframeMap} />
        )}

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button type="submit" className="w-full">
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
